/**
 * Custom hook to calculate the take rate percentage for a given product and its quantity.
 *
 * This hook uses various utilities and context providers to determine the take rate percentage
 * based on the product's price, quantity, and the user's consumption tier.
 *
 * @param {Product} product - The product for which the take rate percentage is being calculated.
 * @param {ProductQuantity[]} productQuantity - An array of product quantities.
 * @returns {number} - The calculated take rate percentage.
  */

import { useRouter } from 'next/router'

import { useCart } from '../components/Cart/CartProvider'
import { getCountryAndLocaleStrings } from '../utils/locales'
import { getCountryCurrency, getTotalProductQuantity } from '../lib/product'
import useExchangeRate from './useExchangeRate'
import { getCartMaxPrice } from '../lib/cart'
import { libraryFunctions } from '../lib/resources/price'
import { Product, ProductQuantity } from '../utils/types/Product'
import { useAuth } from './useAuth'

const { getTakeRatePercentage, getDefaultPrice } = libraryFunctions

const useProductTakeRate = (
  product: Product,
  productQuantity: ProductQuantity[],
): number => {
  const { consumptionTier } = useAuth()
  const { modifiedCart } = useCart()
  const { locale: countryAndLocaleString } = useRouter()

  const { country } = getCountryAndLocaleStrings(countryAndLocaleString)
  const currentCurrencySymbol = getCountryCurrency(country)
  const exchangeRate = useExchangeRate(currentCurrencySymbol)

  const selectedQuantity = getTotalProductQuantity(productQuantity)
  const defaultPriceObj = getDefaultPrice(product.price)
  const defaultPrice = Number(defaultPriceObj?.['price.value']) + Number(defaultPriceObj?.['price.rebate'])
  const cartTotal = modifiedCart ? getCartMaxPrice(modifiedCart) : 0

  const tier = ((consumptionTier ?? 0)
    + (defaultPrice * selectedQuantity * (1 / (exchangeRate || 1)))
    + (cartTotal ?? 0) * (1 / (exchangeRate || 1))
    - (0 * (1 / (exchangeRate || 1))))

  // Fill up to minimum quantity as lower quantity is not possible
  const moqFillUp = selectedQuantity < Number(defaultPriceObj?.['price.quantity'])
    ? (defaultPrice * (Number(defaultPriceObj?.['price.quantity']) - selectedQuantity)) / (exchangeRate || 1)
    : 0

  return getTakeRatePercentage(tier + moqFillUp)
}

export default useProductTakeRate
